import React, { useState } from 'react';
import './GeminiExtract.css';

function GeminiExtract({ onBack }) {
  const [isDragging, setIsDragging] = useState(false);
  const [uploadedFile, setUploadedFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [extractedData, setExtractedData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [extractionType, setExtractionType] = useState('general');

  const extractionTypes = [
    { id: 'general', name: 'General Text Extraction', description: 'Extract all visible text from the document' },
    { id: 'form', name: 'Form Field Extraction', description: 'Extract labeled fields and their values' },
    { id: 'table', name: 'Table Extraction', description: 'Extract tabular data into structured format' },
    { id: 'handwriting', name: 'Handwriting Recognition', description: 'Focus on handwritten text and signatures' }
  ];

  const handleDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    const files = e.dataTransfer.files;
    if (files && files.length > 0) {
      handleFile(files[0]);
    }
  };

  const handleFileInput = (e) => {
    const files = e.target.files;
    if (files && files.length > 0) {
      handleFile(files[0]);
    }
  };

  const handleFile = (file) => {
    const validTypes = ['application/pdf', 'image/png', 'image/jpeg', 'image/jpg', 'image/gif', 'image/webp'];
    if (!validTypes.includes(file.type)) {
      alert('Please upload a PDF or image file (PNG, JPG, GIF, WebP)');
      return;
    }

    setUploadedFile(file);
    setExtractedData(null);

    // Create preview URL for images
    if (file.type.startsWith('image/')) {
      const url = URL.createObjectURL(file);
      setPreviewUrl(url);
    } else {
      setPreviewUrl(null);
    }
  };

  const handleExtract = async () => {
    if (!uploadedFile) return;

    setLoading(true);

    // Simulate Gemini API call
    setTimeout(() => {
      // Mock extracted data based on extraction type
      let mockData;

      if (extractionType === 'general') {
        mockData = {
          type: 'general',
          text: `PURCHASE AGREEMENT

This Purchase Agreement ("Agreement") is entered into as of November 15, 2025, by and between:

SELLER: John A. Smith and Mary B. Smith, husband and wife
Address: 456 Oak Avenue, Grand Rapids, MI 49503

BUYER: Robert J. Williams and Susan M. Williams
Address: 789 Pine Street, Grand Rapids, MI 49504

PROPERTY: The real property located at 456 Oak Avenue, Grand Rapids, Michigan 49503, legally described as:
Lot 15, Block 3, of OAKWOOD SUBDIVISION, according to the recorded plat thereof, Kent County Records.

APN: 41-14-30-178-004

PURCHASE PRICE: Four Hundred Fifty Thousand Dollars ($450,000.00)

EARNEST MONEY: Ten Thousand Dollars ($10,000.00) to be held by Sun Title Agency

CLOSING DATE: December 20, 2025

[Signatures appear at bottom of document]`,
          confidence: 0.96,
          pages: 1
        };
      } else if (extractionType === 'form') {
        mockData = {
          type: 'form',
          fields: [
            { label: 'Seller Name', value: 'John A. Smith and Mary B. Smith', confidence: 0.98 },
            { label: 'Buyer Name', value: 'Robert J. Williams and Susan M. Williams', confidence: 0.97 },
            { label: 'Property Address', value: '456 Oak Avenue, Grand Rapids, MI 49503', confidence: 0.99 },
            { label: 'APN', value: '41-14-30-178-004', confidence: 0.95 },
            { label: 'Purchase Price', value: '$450,000.00', confidence: 0.98 },
            { label: 'Earnest Money', value: '$10,000.00', confidence: 0.97 },
            { label: 'Closing Date', value: 'December 20, 2025', confidence: 0.96 },
            { label: 'Title Company', value: 'Sun Title Agency', confidence: 0.94 }
          ],
          confidence: 0.97,
          pages: 1
        };
      } else if (extractionType === 'table') {
        mockData = {
          type: 'table',
          tables: [
            {
              title: 'Transaction Summary',
              headers: ['Item', 'Amount'],
              rows: [
                ['Purchase Price', '$450,000.00'],
                ['Earnest Money Deposit', '$10,000.00'],
                ['Loan Amount', '$360,000.00'],
                ['Down Payment', '$90,000.00'],
                ['Estimated Closing Costs', '$8,500.00']
              ]
            }
          ],
          confidence: 0.94,
          pages: 1
        };
      } else if (extractionType === 'handwriting') {
        mockData = {
          type: 'handwriting',
          segments: [
            { text: 'John A. Smith', location: 'Seller Signature Line', confidence: 0.89 },
            { text: 'Mary B. Smith', location: 'Seller Signature Line', confidence: 0.87 },
            { text: '11/15/2025', location: 'Date Field', confidence: 0.92 },
            { text: 'Approved - JW', location: 'Margin Note', confidence: 0.78 },
            { text: 'Robert Williams', location: 'Buyer Signature Line', confidence: 0.85 }
          ],
          confidence: 0.86,
          pages: 1,
          note: 'Handwriting recognition confidence varies based on legibility'
        };
      }

      setExtractedData(mockData);
      setLoading(false);
    }, 2500);
  };

  const handleCopyToClipboard = () => {
    let textToCopy = '';

    if (extractedData.type === 'general') {
      textToCopy = extractedData.text;
    } else if (extractedData.type === 'form') {
      textToCopy = extractedData.fields.map(f => `${f.label}: ${f.value}`).join('\n');
    } else if (extractedData.type === 'table') {
      extractedData.tables.forEach(table => {
        textToCopy += `${table.title}\n`;
        textToCopy += table.headers.join('\t') + '\n';
        table.rows.forEach(row => {
          textToCopy += row.join('\t') + '\n';
        });
      });
    } else if (extractedData.type === 'handwriting') {
      textToCopy = extractedData.segments.map(s => `${s.location}: ${s.text}`).join('\n');
    }

    navigator.clipboard.writeText(textToCopy);
    alert('Copied to clipboard!');
  };

  const handleReset = () => {
    setUploadedFile(null);
    setPreviewUrl(null);
    setExtractedData(null);
  };

  const getConfidenceColor = (confidence) => {
    if (confidence >= 0.9) return '#22c55e';
    if (confidence >= 0.8) return '#eab308';
    return '#ef4444';
  };

  return (
    <div className="gemini-extract">
      <div className="extract-header">
        <div>
          <h2>Gemini Visual Extraction</h2>
          <p className="extract-subtitle">Upload any document for AI-powered text and data extraction</p>
        </div>
      </div>

      {!uploadedFile ? (
        <>
          <div className="extraction-type-selector">
            <h3>Select Extraction Type</h3>
            <div className="extraction-types">
              {extractionTypes.map(type => (
                <div
                  key={type.id}
                  className={`extraction-type-card ${extractionType === type.id ? 'selected' : ''}`}
                  onClick={() => setExtractionType(type.id)}
                >
                  <div className="type-radio">
                    <div className={`radio-dot ${extractionType === type.id ? 'active' : ''}`}></div>
                  </div>
                  <div className="type-info">
                    <h4>{type.name}</h4>
                    <p>{type.description}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div
            className={`drop-zone ${isDragging ? 'dragging' : ''}`}
            onDragEnter={handleDragEnter}
            onDragLeave={handleDragLeave}
            onDragOver={handleDragOver}
            onDrop={handleDrop}
          >
            <div className="drop-zone-content">
              <div className="upload-icon">🔴</div>
              <h3>Drag and drop your document here</h3>
              <p className="drop-zone-subtitle">or</p>
              <label htmlFor="gemini-file-input" className="upload-button">
                Browse Files
              </label>
              <input
                id="gemini-file-input"
                type="file"
                accept=".pdf,.png,.jpg,.jpeg,.gif,.webp"
                onChange={handleFileInput}
                style={{ display: 'none' }}
              />
              <p className="file-info">Accepts PDF and image files (PNG, JPG, GIF, WebP)</p>
            </div>
          </div>
        </>
      ) : (
        <div className="extraction-workspace">
          <div className="workspace-header">
            <div className="file-info-bar">
              <span className="file-name">{uploadedFile.name}</span>
              <span className="file-size">({(uploadedFile.size / 1024).toFixed(1)} KB)</span>
              <button className="reset-button" onClick={handleReset}>Upload Different File</button>
            </div>
          </div>

          <div className="workspace-content">
            <div className="preview-panel">
              <h3>Document Preview</h3>
              {previewUrl ? (
                <img src={previewUrl} alt="Document preview" className="preview-image" />
              ) : (
                <div className="pdf-placeholder">
                  <div className="pdf-icon">📄</div>
                  <p>PDF Preview</p>
                  <p className="pdf-name">{uploadedFile.name}</p>
                </div>
              )}

              {!extractedData && !loading && (
                <button className="extract-button" onClick={handleExtract}>
                  <span className="gemini-icon">🔴</span>
                  Extract with Gemini
                </button>
              )}

              {loading && (
                <div className="extracting-indicator">
                  <div className="gemini-spinner"></div>
                  <p>Gemini is analyzing your document...</p>
                </div>
              )}
            </div>

            {extractedData && (
              <div className="results-panel">
                <div className="results-header">
                  <h3>Extracted Data</h3>
                  <div className="results-meta">
                    <span className="confidence-badge" style={{ backgroundColor: getConfidenceColor(extractedData.confidence) }}>
                      {Math.round(extractedData.confidence * 100)}% Confidence
                    </span>
                    <button className="copy-button" onClick={handleCopyToClipboard}>
                      Copy to Clipboard
                    </button>
                  </div>
                </div>

                <div className="results-content">
                  {extractedData.type === 'general' && (
                    <div className="general-text">
                      <pre>{extractedData.text}</pre>
                    </div>
                  )}

                  {extractedData.type === 'form' && (
                    <div className="form-fields">
                      {extractedData.fields.map((field, index) => (
                        <div key={index} className="extracted-field">
                          <label>{field.label}</label>
                          <div className="field-value-row">
                            <input type="text" value={field.value} readOnly />
                            <span
                              className="field-confidence"
                              style={{ color: getConfidenceColor(field.confidence) }}
                            >
                              {Math.round(field.confidence * 100)}%
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}

                  {extractedData.type === 'table' && (
                    <div className="extracted-tables">
                      {extractedData.tables.map((table, tableIndex) => (
                        <div key={tableIndex} className="extracted-table">
                          <h4>{table.title}</h4>
                          <table>
                            <thead>
                              <tr>
                                {table.headers.map((header, i) => (
                                  <th key={i}>{header}</th>
                                ))}
                              </tr>
                            </thead>
                            <tbody>
                              {table.rows.map((row, rowIndex) => (
                                <tr key={rowIndex}>
                                  {row.map((cell, cellIndex) => (
                                    <td key={cellIndex}>{cell}</td>
                                  ))}
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      ))}
                    </div>
                  )}

                  {extractedData.type === 'handwriting' && (
                    <div className="handwriting-results">
                      {extractedData.note && (
                        <p className="handwriting-note">{extractedData.note}</p>
                      )}
                      {extractedData.segments.map((segment, index) => (
                        <div key={index} className="handwriting-segment">
                          <div className="segment-location">{segment.location}</div>
                          <div className="segment-text">{segment.text}</div>
                          <span
                            className="segment-confidence"
                            style={{ color: getConfidenceColor(segment.confidence) }}
                          >
                            {Math.round(segment.confidence * 100)}%
                          </span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                <div className="results-actions">
                  <button className="re-extract-button" onClick={handleExtract}>
                    Re-extract
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      <div className="gemini-info">
        <div className="info-badge">
          <span className="badge-icon">🔴</span>
          <span>Powered by Google Gemini Enterprise AI</span>
        </div>
        <p>Gemini's visual learning AI provides industry-leading document analysis, including extraction from hard-to-read handwriting and complex scanned documents.</p>
      </div>
    </div>
  );
}

export default GeminiExtract;
